/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-01-19 10:53:09
 **/

import { produce } from "immer";
import omit from "lodash-es/omit";
import has from "lodash-es/has";
// import { sha256 } from "@/crypto";
import useConfigure from "./useConfigure";
import ConfigureItem from "./ConfigureItem";

/**
 * The Configure class is a singleton that manages and interacts with a configuration state.
 * It provides methods for setting, retrieving, updating, and deleting configuration entries.
 * This class utilizes an internal dictionary to manage key-value pairs and interacts with
 * an external state for additional functionality.
 */

export default class Configure {
	static #instance: Configure;
	public static get instance() {
		if (!Configure.#instance) {
			Configure.#instance = new Configure();
		}
		return Configure.#instance;
	}

	#key_dict = new Map<string, string>();
	#items: ConfigureItem[] = [];

	private constructor() {}

	/**
	 * Retrieves the list of items.
	 *
	 * @return {Array} The array of items currently stored.
	 */
	public get items() {
		return this.#items;
	}

	/**
	 * Registers a key-value pair into the internal dictionary.
	 *
	 * @param {string} key - The key to be added or updated in the dictionary.
	 * @param {string} value - The value associated with the specified key.
	 * @return {void} Does not return a value.
	 */
	public register_key(key: string, value: string): void {
		this.#key_dict.set(key, value);
	}

	/**
	 * Registers a configuration item and adds it to the collection of items.
	 *
	 * @param {ConfigureItem} item - The configuration item to be registered.
	 * @return {void} Returns nothing.
	 */
	public register_item(item: ConfigureItem): void {
		this.#items.push(item);
	}

	/**
	 * Retrieves an item from the storage that matches the given key.
	 *
	 * @param {TemplateStringsArray} key - The key used to locate the desired item in the storage.
	 * @return {Object|undefined} The item that matches the key, or undefined if no match is found.
	 */
	public storage(key: TemplateStringsArray) {
		return this.#items.find((item) => item.name === key[0]);
	}

	/**
	 * Reads a value associated with the specified key from the item collection.
	 *
	 * @param {string} key The key to search for in the item collection.
	 * @param {T} default_value The default value to return if the key is not found or the item has no associated value.
	 * @return {T} The value associated with the specified key, or the default value if the key is not found.
	 */
	public read_from_item<T = unknown>(key: string, default_value: T) {
		const item = this.#items.find((item) => item.name === key);
		if (item) {
			const data = item.read as T;
			if (data === item.defaultValue) {
				return default_value;
			}
			return data;
		}
		return default_value;
	}

	/**
	 * Updates the `write` property of an item in the internal collection that matches the given key.
	 *
	 * @param {string} key The name of the item to be updated.
	 * @param {T} value The value to be written to the `write` property of the matching item.
	 * @return {void} Does not return a value.
	 */
	public write_to_item<T = unknown>(key: string, value: T) {
		const item = this.#items.find((item) => item.name === key);
		if (item) {
			item.write = value;
		}
	}

	public delete_from_item(key: string) {
		this.#items.find((item) => item.name === key)?.remove();
	}

	/**
	 * Retrieves a value associated with the provided key from the internal key dictionary.
	 * If no value is found, generates a new value using the SHA-256 hash of the key.
	 *
	 * @param {string} key - The key for which the associated value is to be retrieved.
	 * @return {string} The value associated with the key, or a SHA-256 hash of the key if not found.
	 */
	public get_key(key: string): string {
		return this.#key_dict.get(key) ?? key;
	}

	/**
	 * Checks if a given key exists in the configuration state.
	 *
	 * @param {string} key - The key to check in the configuration state.
	 * @return {boolean} Returns true if the key exists, otherwise false.
	 */
	public has(key: string): boolean {
		const { configure } = useConfigure.getState();
		return has(configure, this.get_key(key));
	}

	/**
	 * Retrieves the value associated with the given key. If the key does not exist, the default value is returned.
	 *
	 * @param {string} key - The unique identifier for the value to retrieve.
	 * @param {T | null} [default_value=null] - The value to return if the key does not exist.
	 * @return {T | null} The value associated with the key, or the default value if the key is not found.
	 */
	public get<T>(key: string, default_value: T | null = null): T | null {
		const { configure } = useConfigure.getState();
		return this.has(key) ? (configure[this.get_key(key)] as T) : default_value;
	}

	/**
	 * Sets a value in the configuration state for a given key.
	 *
	 * @param key The key used to store the value in the configuration state.
	 * @param value The value to be associated with the specified key.
	 * @return void
	 */
	public set<T>(key: string, value: T): void {
		useConfigure.setState((state) =>
			produce(state, (draft) => {
				draft.configure[this.get_key(key)] = value;
			})
		);
	}

	/**
	 * Deletes a specific configuration entry identified by the given key.
	 *
	 * @param {string} key - The unique identifier for the configuration entry to be deleted.
	 * @return {void} This method does not return a value.
	 */
	public clear(key: string): void {
		useConfigure.setState((state) =>
			produce(state, (draft) => {
				draft.configure = omit(state, this.get_key(key));
			})
		);
	}

	/**
	 * Clears the persisted storage, removing all data permanently.
	 *
	 * @return {void} No value is returned.
	 */
	public reset(): void {
		useConfigure.persist.clearStorage();
	}
}
